import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, X, Users, MessageSquare, Sparkles,
  Mail, Phone, Loader2, CheckCircle2, Circle,
} from "lucide-react";
import { API } from "@/lib/auth";

/**
 * Pamper Parties editor — two tabs:
 *   1. Content (hero, included list, packages, add-on, CTAs, referral options)
 *   2. Enquiries (submissions list with status flow)
 */
export default function PamperPartyEditor() {
  const [tab, setTab] = useState("content");
  const tabs = [
    { k: "content", l: "Content", Icon: Sparkles },
    { k: "enquiries", l: "Enquiries", Icon: MessageSquare },
  ];
  return (
    <div className="space-y-6" data-testid="admin-pamper-panel">
      <div className="flex gap-1.5 flex-wrap">
        {tabs.map(({ k, l, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`pamper-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              tab === k
                ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--burgundy))]"
            }`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>
      {tab === "content" && <ContentEditor />}
      {tab === "enquiries" && <EnquiriesView />}
    </div>
  );
}


// ---------- Content editor ----------
function ContentEditor() {
  const [c, setC] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/pamper-parties/content`);
      setC(r.data);
      setDirty(false);
    } catch {
      toast.error("Could not load pamper-party content");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (key, value) => { setC((x) => ({ ...x, [key]: value })); setDirty(true); };
  const patchNested = (parent, key, value) => {
    setC((x) => ({ ...x, [parent]: { ...(x[parent] || {}), [key]: value } })); setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        hero: c.hero, included: c.included, included_note: c.included_note,
        packages: c.packages, addon: c.addon,
        gallery: c.gallery, gallery_heading: c.gallery_heading,
        final_cta: c.final_cta, success: c.success,
        consent_label: c.consent_label, referral_options: c.referral_options,
      };
      await axios.put(`${API}/admin/pamper-parties/content`, payload);
      toast.success("Saved");
      setDirty(false); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!c) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Page content</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1">
            Edit every headline, activity, package and price. Changes go live immediately after saving.
          </p>
        </div>
        <button onClick={save} disabled={!dirty || busy} className="btn-pill btn-primary !text-xs disabled:opacity-50">
          <Save size={14} /> {busy ? "Saving…" : "Save all changes"}
        </button>
      </div>

      {/* Hero */}
      <Card title="Hero section">
        <Grid>
          <TextField label="Eyebrow tag" value={c.hero?.eyebrow} onChange={(v) => patchNested("hero", "eyebrow", v)} />
          <TextField label="Primary CTA label" value={c.hero?.primary_cta} onChange={(v) => patchNested("hero", "primary_cta", v)} />
        </Grid>
        <TextField label="Headline" value={c.hero?.headline} onChange={(v) => patchNested("hero", "headline", v)} />
        <TextArea label="Subheading" value={c.hero?.subheading} onChange={(v) => patchNested("hero", "subheading", v)} />
        <ImageUploadField
          label="Hero image"
          value={c.hero?.image}
          onChange={(v) => patchNested("hero", "image", v)}
        />
      </Card>

      {/* Included activities */}
      <Card title="What's included">
        <p className="text-xs text-[hsl(var(--onyx)/0.5)] mb-3">
          Reorder, edit, add or remove. Available icons: sparkles, star, brush, palette, heart, wand, camera, music, cake.
        </p>
        <div className="space-y-2">
          {(c.included || []).map((it, i) => (
            <div key={i} className="grid grid-cols-[auto,120px,1fr,auto] gap-2 items-center">
              <span className="text-xs text-[hsl(var(--onyx)/0.4)] w-4 text-right">{i + 1}</span>
              <input
                value={it.icon || ""}
                onChange={(e) => {
                  const next = [...c.included]; next[i] = { ...it, icon: e.target.value }; patch("included", next);
                }}
                placeholder="icon key"
                className="h-9 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
              />
              <input
                value={it.label || ""}
                onChange={(e) => {
                  const next = [...c.included]; next[i] = { ...it, label: e.target.value }; patch("included", next);
                }}
                className="h-9 px-3 rounded-full bg-white border border-black/10 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
              />
              <div className="flex gap-1">
                <IconBtn onClick={() => {
                  if (i === 0) return; const next = [...c.included]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; patch("included", next);
                }}><ArrowUp size={12} /></IconBtn>
                <IconBtn onClick={() => {
                  if (i === c.included.length - 1) return; const next = [...c.included]; [next[i + 1], next[i]] = [next[i], next[i + 1]]; patch("included", next);
                }}><ArrowDown size={12} /></IconBtn>
                <IconBtn danger onClick={() => patch("included", c.included.filter((_, j) => j !== i))}><X size={12} /></IconBtn>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => patch("included", [...(c.included || []), { icon: "sparkles", label: "New activity" }])}
          className="mt-3 text-xs text-[hsl(var(--burgundy))] hover:underline"
        >
          + Add activity
        </button>
        <TextArea label="Introductory note" value={c.included_note} onChange={(v) => patch("included_note", v)} />
      </Card>

      {/* Packages */}
      <Card title="Party packages">
        <div className="grid gap-4 lg:grid-cols-2">
          {(c.packages || []).map((p, i) => (
            <div key={i} className="rounded-3xl bg-blush/30 p-5 space-y-3 border border-[hsl(var(--onyx)/0.06)]">
              <div className="flex items-center justify-between">
                <TextField label="Package name" value={p.name} onChange={(v) => {
                  const next = [...c.packages]; next[i] = { ...p, name: v }; patch("packages", next);
                }} inline />
                <IconBtn danger onClick={() => patch("packages", c.packages.filter((_, j) => j !== i))}>
                  <Trash2 size={12} />
                </IconBtn>
              </div>
              <Grid>
                <TextField label="Duration (hours)" type="number" value={p.duration_hours} onChange={(v) => {
                  const next = [...c.packages]; next[i] = { ...p, duration_hours: Number(v) || 0 }; patch("packages", next);
                }} />
                <TextField label="Price per child (£)" type="number" value={p.price_per_child} onChange={(v) => {
                  const next = [...c.packages]; next[i] = { ...p, price_per_child: Number(v) || 0 }; patch("packages", next);
                }} />
                <TextField label="Min children" type="number" value={p.min_children} onChange={(v) => {
                  const next = [...c.packages]; next[i] = { ...p, min_children: Number(v) || 0 }; patch("packages", next);
                }} />
                <TextField label="Max children" type="number" value={p.max_children} onChange={(v) => {
                  const next = [...c.packages]; next[i] = { ...p, max_children: Number(v) || 0 }; patch("packages", next);
                }} />
              </Grid>
              <TextArea
                label="Highlights (one per line)"
                value={(p.highlights || []).join("\n")}
                onChange={(v) => {
                  const next = [...c.packages];
                  next[i] = { ...p, highlights: v.split("\n").map((s) => s.trim()).filter(Boolean) };
                  patch("packages", next);
                }}
              />
              <TextField label="CTA button label" value={p.cta} onChange={(v) => {
                const next = [...c.packages]; next[i] = { ...p, cta: v }; patch("packages", next);
              }} />
              <label className="flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.7)]">
                <input
                  type="checkbox"
                  checked={!!p.featured}
                  onChange={(e) => {
                    const next = [...c.packages]; next[i] = { ...p, featured: e.target.checked }; patch("packages", next);
                  }}
                />
                Mark as “Most popular” (dark card)
              </label>
            </div>
          ))}
        </div>
        <button
          onClick={() => patch("packages", [...(c.packages || []), {
            id: `pkg-${Date.now()}`, name: "New package", price_per_child: 40, duration_hours: 2,
            min_children: 4, max_children: 12, highlights: [], cta: "Enquire About This Package",
          }])}
          className="mt-3 text-xs text-[hsl(var(--burgundy))] hover:underline"
        >
          + Add package
        </button>
      </Card>

      {/* Gallery */}
      <Card title="Recent parties mini gallery">
        <p className="text-xs text-[hsl(var(--onyx)/0.5)] mb-3">
          Add photo or video URLs. Videos autoplay muted on loop. Faces should be blurred before uploading.
        </p>
        <TextField label="Section heading" value={c.gallery_heading} onChange={(v) => patch("gallery_heading", v)} />
        <div className="mt-4 space-y-3">
          {(c.gallery || []).map((it, i) => (
            <div key={i} className="grid grid-cols-[auto,1fr,auto,auto] gap-2 items-start p-3 rounded-2xl bg-blush/30 border border-[hsl(var(--onyx)/0.05)]">
              <span className="text-xs text-[hsl(var(--onyx)/0.4)] mt-2 w-4">{i + 1}</span>
              <div className="space-y-2">
                <div className="grid grid-cols-[100px,1fr,auto] gap-2 items-center">
                  <select
                    value={it.type || "image"}
                    onChange={(e) => {
                      const next = [...c.gallery]; next[i] = { ...it, type: e.target.value }; patch("gallery", next);
                    }}
                    className="h-9 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                  <input
                    value={it.url || ""}
                    onChange={(e) => {
                      const next = [...c.gallery]; next[i] = { ...it, url: e.target.value }; patch("gallery", next);
                    }}
                    placeholder="Upload a file or paste a URL"
                    className="h-9 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
                  />
                  <UploadButton
                    onUploaded={({ url, type }) => {
                      const next = [...c.gallery]; next[i] = { ...it, url, type }; patch("gallery", next);
                    }}
                  />
                </div>
                <input
                  value={it.caption || ""}
                  onChange={(e) => {
                    const next = [...c.gallery]; next[i] = { ...it, caption: e.target.value }; patch("gallery", next);
                  }}
                  placeholder="Caption (shown over image)"
                  className="w-full h-9 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <IconBtn onClick={() => {
                  if (i === 0) return; const next = [...c.gallery]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; patch("gallery", next);
                }}><ArrowUp size={12} /></IconBtn>
                <IconBtn onClick={() => {
                  if (i === (c.gallery || []).length - 1) return; const next = [...c.gallery]; [next[i + 1], next[i]] = [next[i], next[i + 1]]; patch("gallery", next);
                }}><ArrowDown size={12} /></IconBtn>
              </div>
              <IconBtn danger onClick={() => patch("gallery", (c.gallery || []).filter((_, j) => j !== i))}>
                <X size={12} />
              </IconBtn>
            </div>
          ))}
        </div>
        <button
          onClick={() => patch("gallery", [...(c.gallery || []), { type: "image", url: "", caption: "" }])}
          className="mt-3 text-xs text-[hsl(var(--burgundy))] hover:underline"
        >
          + Add gallery item
        </button>
      </Card>

      {/* Add-on */}
      <Card title="Optional add-on (Party Bags)">
        <Grid>
          <TextField label="Add-on name" value={c.addon?.name} onChange={(v) => patchNested("addon", "name", v)} />
          <TextField label="Price per child (£)" type="number" value={c.addon?.price_per_child} onChange={(v) => patchNested("addon", "price_per_child", Number(v) || 0)} />
        </Grid>
        <TextArea label="Description" value={c.addon?.description} onChange={(v) => patchNested("addon", "description", v)} />
        <TextArea
          label="Contents (one per line)"
          value={(c.addon?.contents || []).join("\n")}
          onChange={(v) => patchNested("addon", "contents", v.split("\n").map((s) => s.trim()).filter(Boolean))}
        />
      </Card>

      {/* Copy blocks */}
      <Card title="Copy blocks">
        <TextField label="Consent checkbox label" value={c.consent_label} onChange={(v) => patch("consent_label", v)} />
        <TextField label="Referral options (comma separated)" value={(c.referral_options || []).join(", ")}
          onChange={(v) => patch("referral_options", v.split(",").map((s) => s.trim()).filter(Boolean))} />
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="overline mb-2">Final CTA</div>
            <TextField label="Headline" value={c.final_cta?.headline} onChange={(v) => patchNested("final_cta", "headline", v)} />
            <TextArea label="Body" value={c.final_cta?.body} onChange={(v) => patchNested("final_cta", "body", v)} />
            <TextField label="Button label" value={c.final_cta?.button} onChange={(v) => patchNested("final_cta", "button", v)} />
          </div>
          <div>
            <div className="overline mb-2">Success message</div>
            <TextField label="Headline" value={c.success?.headline} onChange={(v) => patchNested("success", "headline", v)} />
            <TextArea label="Body" value={c.success?.body} onChange={(v) => patchNested("success", "body", v)} />
          </div>
        </div>
      </Card>

      <div className="flex justify-end sticky bottom-4 pt-4">
        <button onClick={save} disabled={!dirty || busy} className="btn-pill btn-primary !text-xs disabled:opacity-50 shadow-lg">
          <Save size={14} /> {busy ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
}


// ---------- Enquiries ----------
const STATUS_ORDER = ["new", "contacted", "booked", "closed"];
const STATUS_META = {
  new:       { label: "New",       color: "bg-blush text-[hsl(var(--burgundy))]" },
  contacted: { label: "Contacted", color: "bg-[hsl(var(--gold)/0.2)] text-[hsl(var(--gold))]" },
  booked:    { label: "Booked",    color: "bg-green-100 text-green-700" },
  closed:    { label: "Closed",    color: "bg-[hsl(var(--onyx)/0.06)] text-[hsl(var(--onyx)/0.5)]" },
};

function EnquiriesView() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/pamper-parties/enquiries`);
      setData(r.data);
    } catch { toast.error("Could not load enquiries"); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/pamper-parties/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      if (selected?.id === id) setSelected({ ...selected, status });
      await load();
    } catch { toast.error("Could not update status"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/admin/pamper-parties/enquiries/${id}`);
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  if (!data) return <Loader />;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-5">
        <Stat label="Total" value={data.total} />
        {STATUS_ORDER.map((s) => (
          <Stat key={s} label={STATUS_META[s].label} value={data.counts[s] || 0} />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-5 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4 max-h-[640px] overflow-y-auto">
          <div className="overline mb-3 px-2">Recent enquiries</div>
          {data.enquiries.length === 0 && (
            <div className="text-sm text-[hsl(var(--onyx)/0.5)] px-2">No enquiries yet.</div>
          )}
          <div className="space-y-1">
            {data.enquiries.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.new;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  data-testid={`pamper-enquiry-${e.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                    selected?.id === e.id ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "hover:bg-blush/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{e.parent_name}</span>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-xs opacity-70 mt-0.5 truncate">
                    {e.child_name} · {e.package || "—"}
                  </div>
                  <div className="text-[10px] opacity-60 mt-0.5">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          {!selected && <div className="text-sm text-[hsl(var(--onyx)/0.5)]">Select an enquiry to view details.</div>}
          {selected && (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="overline text-[hsl(var(--gold))]">{selected.reference}</div>
                  <h4 className="font-display text-2xl mt-1">{selected.parent_name}</h4>
                  <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
                    {new Date(selected.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => remove(selected.id)} className="text-xs text-red-500 hover:underline inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </div>

              <div className="flex gap-1.5 flex-wrap">
                {STATUS_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    data-testid={`pamper-status-${s}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
                      selected.status === s
                        ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                        : "bg-blush/40 text-[hsl(var(--onyx)/0.7)] hover:bg-blush"
                    }`}
                  >
                    {selected.status === s ? <CheckCircle2 size={12} /> : <Circle size={12} />} {STATUS_META[s].label}
                  </button>
                ))}
              </div>

              <div className="grid gap-2">
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-sm text-[hsl(var(--burgundy))] hover:underline">
                  <Mail size={14} /> {selected.email}
                </a>
                <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 text-sm text-[hsl(var(--burgundy))] hover:underline">
                  <Phone size={14} /> {selected.phone}
                </a>
              </div>

              <div className="rounded-2xl bg-blush/40 p-4 space-y-2 text-sm">
                <Row label="Child" value={`${selected.child_name}${selected.child_age ? ` (age ${selected.child_age})` : ""}`} />
                <Row label="Package" value={selected.package} />
                <Row label="Date" value={selected.party_date} />
                <Row label="Time" value={selected.party_time} />
                <Row label="Number of children" value={selected.children_count} />
                <Row label="Party bags" value={
                  selected.party_bags === "yes"
                    ? `Yes — ${selected.party_bags_qty || "TBC"} bags`
                    : selected.party_bags
                } />
                <Row label="Allergies / dietary" value={selected.allergies} />
                <Row label="Additional needs" value={selected.additional_needs} />
                <Row label="Special requests" value={selected.special_requests} />
                <Row label="Heard about us" value={selected.referral} />
                <Row label="Extra notes" value={selected.notes} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- helpers ----------
function Loader() {
  return <div className="py-10 flex items-center justify-center text-[hsl(var(--onyx)/0.4)]"><Loader2 className="animate-spin" size={18} /></div>;
}

function UploadButton({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useState({ current: null })[0];
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Warn about huge files before we even try
    if (file.size > 12 * 1024 * 1024) {
      toast.error(`File too big (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 12 MB — please compress or pick a smaller photo.`);
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "upload.jpg");
      const r = await axios.post(`${API}/admin/upload/pamper`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 min for slow mobile connections
      });
      onUploaded(r.data);
      toast.success("Uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "Upload timed out — try a smaller photo or a faster connection" : "")
        || err?.message
        || "Upload failed";
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.warn("pamper upload err:", err);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label
      data-testid="pamper-upload-btn"
      className={`inline-flex items-center justify-center h-9 px-3 rounded-full bg-[hsl(var(--burgundy))] text-white text-[10px] uppercase tracking-widest cursor-pointer whitespace-nowrap ${busy ? "opacity-60 pointer-events-none" : "hover:opacity-90"}`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <><Plus size={12} /> Upload</>}
      <input type="file" accept="image/*,video/*,.heic,.heif" onChange={pick} className="hidden" />
    </label>
  );
}

function ImageUploadField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Upload a photo or paste a URL"
          className="flex-1 h-10 px-4 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]"
        />
        <UploadButton onUploaded={({ url }) => onChange(url)} />
      </div>
      {value ? (
        <img
          src={value}
          alt="preview"
          className="mt-2 h-24 w-24 object-cover rounded-xl border border-black/10"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : null}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
      <div className="overline">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid gap-3 sm:grid-cols-2">{children}</div>; }
function IconBtn({ onClick, danger, children }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
        danger ? "hover:bg-red-50 text-[hsl(var(--onyx)/0.5)] hover:text-red-500"
               : "hover:bg-blush/60 text-[hsl(var(--onyx)/0.6)]"
      }`}
    >{children}</button>
  );
}
function TextField({ label, value, onChange, type = "text", inline }) {
  return (
    <label className={`block ${inline ? "w-full" : ""}`}>
      <span className="overline">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-10 rounded-full bg-white border border-black/10 px-4 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <textarea
        value={value ?? ""}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-2xl bg-white border border-black/10 px-4 py-3 text-sm outline-none focus:border-[hsl(var(--burgundy))] resize-none"
      />
    </label>
  );
}
function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4">
      <div className="overline">{label}</div>
      <div className="font-display text-2xl mt-1">{value ?? 0}</div>
    </div>
  );
}
function Row({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">{label}</div>
      <div className="text-[hsl(var(--onyx)/0.85)] mt-0.5 whitespace-pre-wrap">{value}</div>
    </div>
  );
}
